import React from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';

import { AuthProvider, useAuth } from './context/AuthContext';
import { SocketProvider } from './context/SocketContext';
import { ThemeProvider } from './context/ThemeContext';
import { ToastProvider } from './context/ToastContext';

// ==================== PUBLIC PAGES ====================
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';

// ==================== LAYOUT ====================
import MainLayout from './components/Layout/MainLayout';

// ==================== APP PAGES ====================
import HomePage from './pages/HomePage';
import ChatPage from './pages/ChatPage';
import CommunitiesPage from './pages/CommunitiesPage';
import FeedPage from './pages/FeedPage';
import ProfilePage from './pages/ProfilePage';
import ExploreCommunitiesPage from './pages/ExploreCommunitiesPage';
import AIPage from './pages/AIPage';
import SettingsPage from './pages/SettingsPage';
import GroupsPage from './pages/GroupsPage';
import DocumentsPage from './pages/DocumentsPage';
import MediaPage from './pages/MediaPage';
import StoriesPage from './pages/StoriesPage';
import CallsPage from './pages/CallsPage';

// ======================================================
// NOTIFICATIONS
// ======================================================

function NotificationsPage() {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        gap: 16,
        padding: 40,
        color: 'var(--text-400)',
        textAlign: 'center',
      }}
    >
      <div style={{ fontSize: '3rem' }}>🔔</div>

      <h2
        style={{
          fontFamily: "'Space Grotesk', sans-serif",
          fontSize: '1.2rem',
          fontWeight: 700,
          margin: 0,
        }}
      >
        Notifications
      </h2>

      <p
        style={{
          fontSize: '.875rem',
          maxWidth: 300,
          margin: 0,
        }}
      >
        You're all caught up! No new notifications.
      </p>
    </div>
  );
}

// ======================================================
// APP LOADER
// ======================================================

function AppLoader() {
  return (
    <div
      style={{
        width: '100%',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 20,
        background: 'var(--bg-900)',
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: 16,
          background:
            'linear-gradient(135deg, #6C63FF, #FF4D8D)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontSize: 22,
          boxShadow: '0 0 28px rgba(108,99,255,.5)',
        }}
      >
        ⚡
      </div>

      <div
        className="loader"
        style={{
          width: 20,
          height: 20,
          borderWidth: 2,
        }}
      />
    </div>
  );
}

// ======================================================
// PRIVATE ROUTE
//
// If user is NOT logged in:
//     ALWAYS go to Landing Page (/)
//
// This is important for logout.
// ======================================================

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <AppLoader />;
  }

  // IMPORTANT:
  // Do NOT send to /login here.
  // Send to Landing Page.
  if (!user) {
    return <Navigate to="/" replace />;
  }

  return children;
}

// ======================================================
// PUBLIC ROUTE
//
// If already logged in and they try to open:
// /
// /login
// /register
//
// send them to /home.
// ======================================================

function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <AppLoader />;
  }

  if (user) {
    return <Navigate to="/home" replace />;
  }

  return children;
}

// ======================================================
// AUTHENTICATED APPLICATION
// ======================================================

function AuthenticatedApp() {
  return (
    <PrivateRoute>
      <SocketProvider>
        <MainLayout />
      </SocketProvider>
    </PrivateRoute>
  );
}

// ======================================================
// ROUTES
// ======================================================

function AppRoutes() {
  return (
    <Routes>

      {/* ================================================
          LANDING PAGE
      ================================================ */}

      <Route
        path="/"
        element={
          <PublicRoute>
            <LandingPage />
          </PublicRoute>
        }
      />

      {/* ================================================
          LOGIN
      ================================================ */}

      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* ================================================
          REGISTER
      ================================================ */}

      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />

      {/* ================================================
          AUTHENTICATED APPLICATION
      ================================================ */}

      <Route element={<AuthenticatedApp />}>

        {/* HOME */}
        <Route
          path="/home"
          element={<HomePage />}
        />

        {/* CHAT */}
        <Route
          path="/chat"
          element={<ChatPage />}
        />

        <Route
          path="/chat/:userId"
          element={<ChatPage />}
        />

        {/* GROUPS */}
        <Route
          path="/groups"
          element={<GroupsPage />}
        />

        {/* COMMUNITIES */}
        <Route
          path="/communities"
          element={<CommunitiesPage />}
        />

        <Route
          path="/communities/:communityId"
          element={<CommunitiesPage />}
        />

        <Route
          path="/communities/:communityId/:channel"
          element={<CommunitiesPage />}
        />

        <Route
          path="/explore"
          element={<ExploreCommunitiesPage />}
        />

        {/* FEED */}
        <Route
          path="/feed"
          element={<FeedPage />}
        />

        {/* AI */}
        <Route
          path="/ai"
          element={<AIPage />}
        />

        {/* PROFILE */}
        <Route
          path="/profile"
          element={<ProfilePage />}
        />

        <Route
          path="/profile/:userId"
          element={<ProfilePage />}
        />

        {/* SETTINGS */}
        <Route
          path="/settings"
          element={<SettingsPage />}
        />

        {/* DOCUMENTS */}
        <Route
          path="/documents"
          element={<DocumentsPage />}
        />

        {/* MEDIA */}
        <Route
          path="/media"
          element={<MediaPage />}
        />

        {/* STORIES */}
        <Route
          path="/stories"
          element={<StoriesPage />}
        />

        {/* CALLS */}
        <Route
          path="/calls"
          element={<CallsPage />}
        />

        {/* NOTIFICATIONS */}
        <Route
          path="/notifications"
          element={<NotificationsPage />}
        />

      </Route>

      {/* ================================================
          UNKNOWN URL
      ================================================ */}

      <Route
        path="*"
        element={<Navigate to="/" replace />}
      />

    </Routes>
  );
}

// ======================================================
// APP
// ======================================================

export default function App() {
  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <ThemeProvider>
        <ToastProvider>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </ToastProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}